import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { errorNotify } from 'app/store/common-effects/notifier.effects';
import { LanguageService } from 'core/services/language.service';
import { LoaderService } from 'core/services/loader.service';
import { combineLatest, defer, EMPTY, merge, Observable, of, Subject } from 'rxjs';
import { filter, first, map, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private subject$ = new Subject();

  constructor(
    private title: Title,
    private router: Router,
    private route: ActivatedRoute,
    private loaderService: LoaderService,
    private store$: Store,
    private languageService: LanguageService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    this.subscribeForLoaderChange();
    this.subscribeForTitleChange();

    this.languageService.initialize();
  }

  ngOnDestroy(): void {
    this.subject$.next(undefined);
    this.subject$.complete();
  }

  get loading$(): Observable<boolean> {
    return this.loaderService.isLoading$;
  }

  /**
   * Подписывается на события роутера для того чтобы показывать
   * loader при переходе с роута на роут.
   */
  private subscribeForLoaderChange(): void {
    this.router.events
      .pipe(
        filter(e => e instanceof NavigationStart),
        tap(() => this.loaderService.begin()),
        takeUntil(this.subject$),
      )
      .subscribe();

    this.router.events
      .pipe(
        filter((e) => {
          return e instanceof NavigationEnd
            || e instanceof NavigationError
            || e instanceof NavigationCancel;
        }),
        tap((e) => {
          if (e instanceof NavigationError) {
            this.store$.dispatch(errorNotify({ message: 'ERROR.navigation' }));
          }

          this.loaderService.end();
        }),
        takeUntil(this.subject$),
      )
      .subscribe();
  }

  /**
   * Подписывается на события роутера для того чтобы менять
   * текст в имени закладки.
   */
  private subscribeForTitleChange(): void {
    const routeTitle = this.router.events
      .pipe(
        filter(e => e instanceof NavigationEnd),
        startWith(null),
        map(() => {
          let current = this.route;
          let child = current.children.find(route => route.outlet === 'primary');
          while (child) {
            current = child;
            child = current.children.find(route => route.outlet === 'primary');
          }
          return current.snapshot.data.title;
        }),
      );
    const languageReady = merge(this.translateService.onLangChange, defer(() => {
      const language = this.translateService.currentLang;
      return language && this.translateService.translations[language] ? of(null) : EMPTY;
    }));

    combineLatest([routeTitle, languageReady]).pipe(
      map(([key]) => key),
      filter((key): key is string => typeof key === 'string' && key.length > 0),
      switchMap(key => this.translateService.get(key).pipe(first())),
      takeUntil(this.subject$),
    ).subscribe(title => this.title.setTitle(title));
  }
}
